// Imagens e arquivos anexados à demanda.
//
// Existia o upload na criação (POST /demandas/:id/anexos desde 31/08), mas a
// imagem não aparecia em lugar nenhum depois — a Lorraine anexou e perguntou
// "onde ela aparece?" (08/09). Esta seção é a resposta: galeria visível na
// tela da demanda, com quem enviou, e a possibilidade de anexar mais depois
// (antes só dava na hora de criar).
import { useEffect, useRef, useState } from 'react';
import { Upload, X, FileText, Film, Download, Trash2, ImageOff } from 'lucide-react';
import { format } from 'date-fns';
import { api } from '../../api/client';

const MAX_MB = 20;

function ehImagem(a) {
  return (a.mime || '').startsWith('image/');
}

function tamanhoTexto(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function AnexosSection({ demanda, anexos: anexosIniciais, podeEditar = true, onChange }) {
  const demandaId = demanda.codigo || demanda.id;
  const [anexos, setAnexos] = useState(anexosIniciais || []);
  const [uploading, setUploading] = useState(null); // { nome, progresso, indice, total }
  const [erro, setErro] = useState('');
  const [ampliada, setAmpliada] = useState(null);
  const [removendo, setRemovendo] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { setAnexos(anexosIniciais || []); }, [anexosIniciais]);

  const recarregar = async () => {
    const r = await api.get(`/demandas/${demandaId}/anexos`);
    setAnexos(r.anexos);
    onChange?.(r.anexos);
  };

  const enviar = async (fileList) => {
    const arquivos = Array.from(fileList || []);
    if (!arquivos.length) return;
    setErro('');
    const falhas = [];
    for (let i = 0; i < arquivos.length; i++) {
      const arquivo = arquivos[i];
      if (arquivo.size > MAX_MB * 1024 * 1024) {
        falhas.push(`${arquivo.name} (maior que ${MAX_MB}MB)`);
        continue;
      }
      setUploading({ nome: arquivo.name, progresso: 0, indice: i + 1, total: arquivos.length });
      try {
        const up = await api.upload('/uploads', arquivo, (p) => setUploading((u) => (u ? { ...u, progresso: p } : u)));
        await api.post(`/demandas/${demandaId}/anexos`, {
          url: up.url, nome: up.originalName, mime: up.mime, tamanho: up.size,
        });
      } catch (e) {
        falhas.push(`${arquivo.name} (${e.data?.error || e.message})`);
      }
    }
    setUploading(null);
    if (falhas.length) setErro(`Não consegui anexar: ${falhas.join('; ')}`);
    await recarregar();
  };

  const remover = async (id) => {
    setRemovendo(null);
    setErro('');
    try {
      await api.del(`/demandas/${demandaId}/anexos/${id}`);
      await recarregar();
    } catch (e) {
      setErro(e.data?.error || e.message);
    }
  };

  const imagens = anexos.filter(ehImagem);

  return (
    <div className="stack" style={{ gap: 10 }}>
      {anexos.length === 0 && !uploading && (
        <div className="text-sm muted row" style={{ gap: 6 }}>
          <ImageOff size={14} /> Nenhuma imagem ou arquivo anexado.
        </div>
      )}

      {anexos.length > 0 && (
        <div className="anexo-grid">
          {anexos.map((a) => {
            const imagem = ehImagem(a);
            const Icone = (a.mime || '').startsWith('video/') ? Film : FileText;
            return (
              <div key={a.id} className="anexo-item">
                {imagem ? (
                  <button
                    type="button"
                    className="anexo-thumb"
                    onClick={() => setAmpliada(a)}
                    title={`Ver ${a.nome}`}
                  >
                    <img src={a.caminho} alt={a.nome} loading="lazy" />
                  </button>
                ) : (
                  <a className="anexo-thumb anexo-thumb-file" href={a.caminho} target="_blank" rel="noreferrer" title={`Abrir ${a.nome}`}>
                    <Icone size={26} />
                    <span className="text-xs" style={{ marginTop: 4 }}>{(a.mime || '').split('/')[1]?.toUpperCase() || 'ARQUIVO'}</span>
                  </a>
                )}
                <div className="anexo-meta">
                  <span className="anexo-nome" title={a.nome}>{a.nome}</span>
                  <span className="text-xs muted">
                    {a.autor_nome?.split(' ')[0]} · {format(new Date(a.created_at), 'dd/MM HH:mm')}
                    {a.tamanho ? ` · ${tamanhoTexto(a.tamanho)}` : ''}
                  </span>
                </div>
                <div className="anexo-acoes">
                  <a className="btn btn-ghost btn-icon btn-sm" href={a.caminho} target="_blank" rel="noreferrer" title="Abrir/baixar">
                    <Download size={13} />
                  </a>
                  {podeEditar && (
                    removendo === a.id ? (
                      <>
                        <button className="btn btn-danger btn-sm" style={{ padding: '2px 8px', minHeight: 24, fontSize: 11 }} onClick={() => remover(a.id)}>Remover</button>
                        <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', minHeight: 24, fontSize: 11 }} onClick={() => setRemovendo(null)}>Não</button>
                      </>
                    ) : (
                      <button className="btn btn-ghost btn-icon btn-sm" title="Remover anexo" onClick={() => setRemovendo(a.id)}>
                        <Trash2 size={13} />
                      </button>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {uploading && (
        <div>
          <div style={{ height: 4, background: 'var(--c-border)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ width: `${uploading.progresso}%`, height: '100%', background: 'var(--c-primary)', transition: 'width .2s' }} />
          </div>
          <div className="text-xs muted">
            Enviando {uploading.indice}/{uploading.total} — {uploading.nome} ({uploading.progresso}%)
          </div>
        </div>
      )}

      {erro && <div className="text-sm" style={{ color: 'var(--c-danger)' }}>{erro}</div>}

      {podeEditar && (
        <div className="row" style={{ gap: 8 }}>
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf,video/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => { enviar(e.target.files); e.target.value = ''; }}
          />
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()} disabled={!!uploading}>
            <Upload size={13} /> Anexar imagens
          </button>
          <span className="text-xs muted" style={{ alignSelf: 'center' }}>
            Imagem, PDF ou vídeo · até {MAX_MB}MB cada
          </span>
        </div>
      )}

      {ampliada && (
        <div className="anexo-lightbox" onClick={() => setAmpliada(null)} role="presentation">
          <button type="button" className="anexo-lightbox-fechar" onClick={() => setAmpliada(null)} title="Fechar">
            <X size={20} />
          </button>
          <img src={ampliada.caminho} alt={ampliada.nome} onClick={(e) => e.stopPropagation()} />
          <div className="anexo-lightbox-legenda">
            {ampliada.nome}
            {imagens.length > 1 && ` · ${imagens.findIndex((i) => i.id === ampliada.id) + 1} de ${imagens.length}`}
          </div>
        </div>
      )}
    </div>
  );
}
